import { IconButton } from "@mui/material"
import { EditOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"

import { ACCOUNT_SUBTYPE_LABELS, ACCOUNT_TYPE_LABELS } from "../constants"
import { formatGlAccountLabel } from "../utils/formatGlAccountLabel"
import { formatGlAccountNumber } from "../utils/formatGlAccountNumber"

import StyledChip from "@/components/common/Chip/Chip"
import { StatusIndicator } from "@/components/common/StatusIndicator/StatusIndicator"
import type { GlAccount } from "@/graphql/codegen/graphql"
import { GlAccountType } from "@/graphql/codegen/graphql"

interface UseChartOfAccountsColumnsProps {
  onEdit?: (account: GlAccount) => void
}

export const useChartOfAccountsColumns = ({ onEdit }: UseChartOfAccountsColumnsProps): ColumnDef<GlAccount>[] => {
  const columns = useMemo<ColumnDef<GlAccount>[]>(
    () => [
      {
        accessorKey: "number",
        header: "Account #",
        cell: ({ row }) => {
          const { number, subNumber } = row.original
          const displayNumber = formatGlAccountNumber({ number, subNumber })

          return (
            <div className='flex items-center gap-2 font-medium'>
              <StatusIndicator
                status={Boolean(row.original.isActive)}
                map={
                  new Map([
                    [true, "success"],
                    [false, "secondary"],
                  ])
                }
              />
              {displayNumber}
            </div>
          )
        },
        enableSorting: true,
        sortDescFirst: false,
        size: 120,
      },
      {
        accessorKey: "title",
        header: "Account Title",
        cell: ({ row }) => <div>{row.original.title}</div>,
        enableSorting: true,
        size: 250,
      },
      {
        id: "parentAccount",
        accessorKey: "parentAccount",
        header: "Parent Account",
        cell: ({ row }) => {
          const { parent } = row.original
          const parentAccountNumber = parent ? formatGlAccountLabel(parent) : null
          return <div>{parentAccountNumber || "-"}</div>
        },
        enableSorting: true,
        sortDescFirst: false,
        size: 150,
      },
      {
        accessorKey: "company",
        header: "Company",
        cell: ({ row }) => <div>{row.original.companyName}</div>,
        enableSorting: false,
        size: 200,
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => {
          const type = row.original.type
          if (!type) return <div>-</div>

          const label = ACCOUNT_TYPE_LABELS[type]

          switch (type) {
            case GlAccountType.Asset:
              return <StyledChip label={label} className='bg-primary-200 text-gray-900' size='small' />
            case GlAccountType.Equity:
              return <StyledChip label={label} className='bg-tertiary-purple-300 text-gray-900' size='small' />
            case GlAccountType.Liability:
              return <StyledChip label={label} className='bg-secondary-200 text-gray-900' size='small' />
            case GlAccountType.Revenue:
              return <StyledChip label={label} className='bg-tertiary-green-100 text-gray-900' size='small' />
            case GlAccountType.Expense:
              return <StyledChip label={label} className='bg-tertiary-orange-300 text-gray-900' size='small' />
            default:
              return <StyledChip label={label} className='bg-gray-100 text-gray-900' size='small' />
          }
        },
        enableSorting: true,
        size: 120,
      },
      {
        accessorKey: "subtype",
        header: "Subtype",
        cell: ({ row }) => {
          const subType = row.original.subType
          return <div>{subType ? ACCOUNT_SUBTYPE_LABELS[subType] : "-"}</div>
        },
        enableSorting: false,
        size: 150,
      },
      {
        id: "actions",
        header: "",
        size: 30,
        enableSorting: false,
        cell: ({ row }) =>
          onEdit ? (
            <IconButton
              data-testid={`edit-account-${row.original.number}`}
              size='small'
              onClick={() => onEdit(row.original)}
            >
              <EditOutlined />
            </IconButton>
          ) : null,
      },
      {
        id: "expand",
        header: "",
        size: 30,
        enableSorting: false,
        cell: ({ row }) => (
          <IconButton
            data-testid={`expand-row-${row.original.number}`}
            size='small'
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        ),
      },
    ],
    [onEdit]
  )

  return columns
}
